import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Invoice, UpdateInvoiceRequest, InvoiceStatus, Currency, InvoiceType, UnitOfMeasure, TaxRate, TaxRateLabels, PaymentMethod } from '../types/invoice';
import { invoiceService } from '../services/invoiceService';
import { companyService } from '../services/companyService';
import { counterpartyService } from '../services/counterpartyService';
import { useTranslation } from '../hooks/useTranslation';
import { Language, PDF_LANGUAGE_OPTIONS } from '../types/language';
import { useLanguage } from '../contexts/LanguageContext';
import { SearchableSelect } from './SearchableSelect';
import { isForeignEuCountry } from '../utils/euCountry';

interface EditInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  companyId: string;
  onSuccess: () => void;
  onInvoiceUpdated?: () => void;
}

const EditInvoiceModal: React.FC<EditInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice,
  companyId,
  onSuccess,
  onInvoiceUpdated
}) => {
  const { t } = useTranslation();
  const { language: systemLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    clientId: '',
    issueDate: '',
    dueDateDays: 14,
    saleDate: new Date().toISOString().split('T')[0],
    placeOfIssue: '',
    invoiceType: InvoiceType.STANDARD,
    exchangeRate: 0,
    exchangeRateTable: '',
    exchangeRateDate: '',
    reverseCharge: false,
    bankAccountId: '',
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    status: InvoiceStatus.DRAFT,
    currency: Currency.PLN,
    notes: '',
    internalComment: '',
    pdfLanguage: systemLanguage || Language.PL_ONLY,
    items: [] as Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      taxRate: TaxRate;
      unitOfMeasure: UnitOfMeasure;
    }>
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showExchangeFields, setShowExchangeFields] = useState(false);

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companyService.getCompany(companyId),
    enabled: !!companyId
  });

  const { data: counterparties } = useQuery({
    queryKey: ['counterparties', companyId],
    queryFn: () => counterpartyService.getCompanyCounterparties(companyId),
    enabled: !!companyId
  });

  useEffect(() => {
    if (invoice) {
      // Рассчитываем количество дней до оплаты
      // Обрабатываем даты в формате массива [год, месяц, день] или строки
      const parseDate = (dateValue: string | number[] | undefined): Date | null => {
        if (!dateValue) return null;
        if (Array.isArray(dateValue) && dateValue.length >= 3) {
          const [year, month, day] = dateValue;
          return new Date(year, month - 1, day); // month - 1, так как в JS месяцы начинаются с 0
        }
        if (typeof dateValue === 'string') {
          return new Date(dateValue);
        }
        return null;
      };
      
      const issueDate = parseDate(invoice.issueDate);
      const dueDate = invoice.dueDate ? parseDate(invoice.dueDate) : null;
      const dueDateDays = (issueDate && dueDate) ? Math.ceil((dueDate.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24)) : 14;
      
      // Форматируем даты для input type="date" (YYYY-MM-DD)
      const formatDateForInput = (dateValue: string | undefined | null | Date | number[]): string => {
        if (!dateValue) return '';
        
        // Если это массив [год, месяц, день] (формат с бэкенда)
        if (Array.isArray(dateValue) && dateValue.length >= 3) {
          const [year, month, day] = dateValue;
          if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
            const yearStr = String(year);
            const monthStr = String(month).padStart(2, '0');
            const dayStr = String(day).padStart(2, '0');
            return `${yearStr}-${monthStr}-${dayStr}`;
          }
        }
        
        // Если это уже Date объект, конвертируем в строку
        if (dateValue instanceof Date) {
          if (isNaN(dateValue.getTime())) return '';
          const year = dateValue.getFullYear();
          const month = String(dateValue.getMonth() + 1).padStart(2, '0');
          const day = String(dateValue.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        
        // Проверяем, что это строка
        if (typeof dateValue !== 'string') {
          return '';
        }
        
        // Если дата уже в формате YYYY-MM-DD, возвращаем как есть
        if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue;
        }
        
        // Иначе парсим и форматируем
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setFormData({
        invoiceNumber: invoice.invoiceNumber || '',
        clientId: invoice.clientId || '',
        issueDate: formatDateForInput(invoice.issueDate),
        dueDateDays: dueDateDays,
        saleDate: formatDateForInput(invoice.saleDate),
        placeOfIssue: invoice.placeOfIssue || '',
        invoiceType: invoice.invoiceType || InvoiceType.STANDARD,
        exchangeRate: invoice.exchangeRate || 0,
        exchangeRateTable: invoice.exchangeRateTable || '',
        exchangeRateDate: invoice.exchangeRateDate || '',
        reverseCharge: invoice.reverseCharge || false,
        bankAccountId: invoice.bankAccountId || '',
        paymentMethod: invoice.paymentMethod || PaymentMethod.BANK_TRANSFER,
        status: invoice.status || InvoiceStatus.DRAFT,
        currency: invoice.currency || Currency.PLN,
        notes: invoice.notes || '',
        internalComment: invoice.internalComment || '',
        pdfLanguage: (invoice.pdfLanguage as Language) || systemLanguage || Language.PL_ONLY,
        items: invoice.items?.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || TaxRate.VAT_23,
          unitOfMeasure: item.unitOfMeasure || UnitOfMeasure.PIECE
        })) || []
      });
      
      // Показываем поля курса валют если они заполнены
      setShowExchangeFields(
        !!(invoice.exchangeRate || invoice.exchangeRateTable || invoice.exchangeRateDate)
      );
    }
  }, [invoice, systemLanguage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;

    // Проверяем, можно ли редактировать фактуру (только DRAFT)
    if (invoice.status !== InvoiceStatus.DRAFT) {
      setError(t('invoices.cannotEditNonDraft') || 'Эта фактура не может быть отредактирована, так как её статус отличен от "Черновик". Можно изменить только статус фактуры.');
      return;
    }

    // Запрещаем изменение статуса на DRAFT, если фактура уже была отправлена
    const wasSent = invoice.status !== InvoiceStatus.DRAFT && invoice.status !== InvoiceStatus.CANCELLED;
    if (wasSent && formData.status === InvoiceStatus.DRAFT) {
      setError(t('invoices.cannotChangeToDraft') || 'Нельзя изменить статус фактуры на "Черновик", если она уже была отправлена.');
      return;
    }

    // Валидация обязательных полей
    if (!formData.placeOfIssue.trim()) {
      setError(t('invoices.validation.placeOfIssueRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Рассчитываем дату оплаты на основе количества дней
      const issueDate = new Date(formData.issueDate);
      const dueDate = new Date(issueDate);
      dueDate.setDate(issueDate.getDate() + formData.dueDateDays);
      
      const updateRequest: UpdateInvoiceRequest = {
        invoiceNumber: formData.invoiceNumber,
        clientId: formData.clientId,
        issueDate: formData.issueDate,
        dueDate: dueDate.toISOString().split('T')[0],
        saleDate: formData.saleDate && formData.saleDate.trim() !== '' ? formData.saleDate : undefined,
        placeOfIssue: formData.placeOfIssue,
        invoiceType: formData.invoiceType,
        exchangeRate: formData.exchangeRate && formData.exchangeRate > 0 ? formData.exchangeRate : undefined,
        exchangeRateTable: formData.exchangeRateTable && formData.exchangeRateTable.trim() !== '' ? formData.exchangeRateTable : undefined,
        exchangeRateDate: formData.exchangeRateDate && formData.exchangeRateDate.trim() !== '' ? formData.exchangeRateDate : undefined,
        reverseCharge: formData.reverseCharge,
        bankAccountId: formData.bankAccountId && formData.bankAccountId.trim() !== '' ? formData.bankAccountId : undefined,
        paymentMethod: formData.paymentMethod,
        status: formData.status,
        currency: formData.currency,
        notes: formData.notes || undefined,
        internalComment: formData.internalComment || undefined,
        pdfLanguage: formData.pdfLanguage,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || undefined,
          unitOfMeasure: item.unitOfMeasure
        }))
      };

      await invoiceService.updateInvoice(companyId, invoice.id, updateRequest);
      onSuccess();
      onInvoiceUpdated?.(); // Вызываем обновление данных инвойсов
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка при обновлении инвойса');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;

    const isDraftInvoice = invoice.status === InvoiceStatus.DRAFT;
    if (!isDraftInvoice) {
      setError(
        t('invoices.deleteInvoiceOnlySample') ||
          'Удаление разрешено только для фактур со статусом Черновик.'
      );
      return;
    }
    if (invoice.wfirmaId) {
      setError(
        t('invoices.deleteInvoiceOnlyNotSentToWfirma') ||
          'Нельзя удалить фактуру, которая уже была отправлена в wFirma.'
      );
      return;
    }
    
    if (!window.confirm(t('invoices.deleteInvoiceConfirm'))) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await invoiceService.deleteInvoice(companyId, invoice.id);
      onSuccess();
      onInvoiceUpdated?.(); // Вызываем обновление данных инвойсов
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка при удалении инвойса');
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = () => {
    const country = counterparties?.find((c) => c.id === formData.clientId)?.country;
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: isForeignEuCountry(country) ? TaxRate.NPUE : TaxRate.NP,
        unitOfMeasure: UnitOfMeasure.PIECE
      }]
    });
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index: number, field: keyof typeof formData.items[0], value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  if (!isOpen || !invoice) return null;

  // Проверяем, можно ли редактировать фактуру (только DRAFT)
  const isEditable = invoice.status === InvoiceStatus.DRAFT;
  const isReadOnly = !isEditable;
  const canDeleteInvoice = invoice.status === InvoiceStatus.DRAFT && !invoice.wfirmaId;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('invoices.editInvoice')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isReadOnly && (
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-yellow-800">
                {t('invoices.cannotEditNonDraft') || 'Эта фактура не может быть отредактирована, так как её статус отличен от "Черновик". Можно изменить только статус фактуры.'}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.invoiceNumber')}
              </label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.clientId')}
              </label>
              <SearchableSelect
                options={counterparties?.map((counterparty) => ({
                  value: counterparty.id,
                  label: counterparty.name,
                  searchText: `${counterparty.name} ${counterparty.taxId || ''} ${counterparty.vatId || ''} ${counterparty.email || ''} ${counterparty.phone || ''} ${counterparty.comment || ''}`.trim()
                })) || []}
                value={formData.clientId}
                onChange={(value) => {
                  const country = counterparties?.find((c) => c.id === value)?.country;
                  const next = isForeignEuCountry(country) ? TaxRate.NPUE : TaxRate.NP;
                  setFormData({
                    ...formData,
                    clientId: value,
                    items: formData.items.map((item) =>
                      !item.taxRate || item.taxRate === TaxRate.NP || item.taxRate === TaxRate.NPUE
                        ? { ...item, taxRate: next }
                        : item
                    ),
                  });
                }}
                placeholder={t('common.selectClient')}
                disabled={isReadOnly}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('companies.bankAccount') || t('common.bankAccount')}
              </label>
              <SearchableSelect
                options={company?.bankAccounts?.map((account) => ({
                  value: account.id,
                  label: `${account.name} - ${account.accountNumber} (${account.currency})`,
                  searchText: `${account.name} ${account.accountNumber} ${account.bankName} ${account.currency}`.trim()
                })) || []}
                value={formData.bankAccountId}
                onChange={(value) => {
                  setFormData({ ...formData, bankAccountId: value });
                  // Автоматически устанавливаем валюту из выбранного счета
                  const selectedAccount = company?.bankAccounts?.find(acc => acc.id === value);
                  if (selectedAccount) {
                    setFormData({ ...formData, bankAccountId: value, currency: selectedAccount.currency });
                  }
                }}
                placeholder={t('common.selectBankAccount')}
                disabled={isReadOnly}
              />
              {formData.bankAccountId && (() => {
                const selectedAccount = company?.bankAccounts?.find(acc => acc.id === formData.bankAccountId);
                if (selectedAccount?.bankAddress) {
                  return (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('companies.bankAddress')}
                      </label>
                      <p className="text-sm text-gray-700">{selectedAccount.bankAddress}</p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.paymentMethod') || 'Способ оплаты'}
              </label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as PaymentMethod })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value={PaymentMethod.BANK_TRANSFER}>
                  {t('invoices.paymentMethods.BANK_TRANSFER') || 'Банковский перевод'}
                </option>
                <option value={PaymentMethod.CASH}>
                  {t('invoices.paymentMethods.CASH') || 'Наличные'}
                </option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.status')}
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as InvoiceStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {/* Опция DRAFT доступна только если текущий статус DRAFT */}
                {invoice.status === InvoiceStatus.DRAFT && (
                  <option value={InvoiceStatus.DRAFT}>{t('invoices.statuses.DRAFT')}</option>
                )}
                <option value={InvoiceStatus.SENT}>{t('invoices.statuses.SENT')}</option>
                <option value={InvoiceStatus.PARTIALLY_PAID}>{t('invoices.statuses.PARTIALLY_PAID')}</option>
                <option value={InvoiceStatus.PAID}>{t('invoices.statuses.PAID')}</option>
                <option value={InvoiceStatus.OVERDUE}>{t('invoices.statuses.OVERDUE')}</option>
                <option value={InvoiceStatus.CANCELLED}>{t('invoices.statuses.CANCELLED')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.currency')}
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as Currency })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value={Currency.PLN}>{t('invoices.currencies.PLN')}</option>
                <option value={Currency.USD}>{t('invoices.currencies.USD')}</option>
                <option value={Currency.EUR}>{t('invoices.currencies.EUR')}</option>
                <option value={Currency.GBP}>{t('invoices.currencies.GBP')}</option>
                <option value={Currency.CAD}>{t('invoices.currencies.CAD')}</option>
                <option value={Currency.CNY}>{t('invoices.currencies.CNY') || 'CNY - Chinese Yuan'}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.dueDate')} (дни)
              </label>
              <input
                type="number"
                min="1"
                value={formData.dueDateDays}
                onChange={(e) => setFormData({ ...formData, dueDateDays: parseInt(e.target.value) || 14 })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>
          </div>

          {/* Дополнительные поля */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.saleDate')}
              </label>
              <input
                type="date"
                value={formData.saleDate}
                onChange={(e) => setFormData({ ...formData, saleDate: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.placeOfIssue')} *
              </label>
              <input
                type="text"
                value={formData.placeOfIssue}
                onChange={(e) => setFormData({ ...formData, placeOfIssue: e.target.value })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.invoiceType')}
              </label>
              <select
                value={formData.invoiceType}
                onChange={(e) => setFormData({ ...formData, invoiceType: e.target.value as InvoiceType })}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value={InvoiceType.STANDARD}>{t('invoices.types.STANDARD')}</option>
                <option value={InvoiceType.PROFORMA}>{t('invoices.types.PROFORMA')}</option>
                <option value={InvoiceType.STANDARD_NO_VAT}>{t('invoices.types.STANDARD_NO_VAT')}</option>
                <option value={InvoiceType.PROFORMA_NO_VAT}>{t('invoices.types.PROFORMA_NO_VAT')}</option>
              </select>
            </div>

            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="showExchangeFields"
                  checked={showExchangeFields}
                  onChange={(e) => setShowExchangeFields(e.target.checked)}
                  disabled={isReadOnly}
                  className="mr-2 disabled:cursor-not-allowed"
                />
                <label htmlFor="showExchangeFields" className="text-sm font-medium text-gray-700">
                  {t('invoices.exchangeRates.showFields')}
                </label>
              </div>
            </div>

            {showExchangeFields && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.exchangeRate')}
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.exchangeRate}
                    onChange={(e) => setFormData({ ...formData, exchangeRate: parseFloat(e.target.value) || 0 })}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.exchangeRateTable')}
                  </label>
                  <input
                    type="text"
                    value={formData.exchangeRateTable}
                    onChange={(e) => setFormData({ ...formData, exchangeRateTable: e.target.value })}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.exchangeRateDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.exchangeRateDate}
                    onChange={(e) => setFormData({ ...formData, exchangeRateDate: e.target.value })}
                    disabled={isReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.reverseCharge')}
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.reverseCharge}
                  onChange={(e) => setFormData({ ...formData, reverseCharge: e.target.checked })}
                  disabled={isReadOnly}
                  className="mr-2 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-600">{t('invoices.reverseCharge')}</span>
              </div>
            </div>
          </div>
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('invoices.items')}</h3>
              <button
                type="button"
                onClick={addItem}
                disabled={isReadOnly}
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                + {t('invoices.addItem')}
              </button>
            </div>

            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('invoices.description')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('invoices.quantity')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('invoices.unitPrice')}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('invoices.taxRate')}
                      </label>
                      {(() => {
                        // Определяем, является ли фактура фактурой без VAT
                        const hasVatId = company?.vatId != null && company.vatId.trim() !== '';
                        const isNoVatInvoice = !hasVatId || 
                                              formData.invoiceType === InvoiceType.STANDARD_NO_VAT || 
                                              formData.invoiceType === InvoiceType.PROFORMA_NO_VAT;
                        
                        // Для фактур без VAT автоматически устанавливаем ZW
                        const currentTaxRate = isNoVatInvoice ? TaxRate.ZW : item.taxRate;
                        
                        // Обновляем taxRate, если это фактура без VAT
                        if (isNoVatInvoice && item.taxRate !== TaxRate.ZW) {
                          setTimeout(() => updateItem(index, 'taxRate', TaxRate.ZW), 0);
                        }
                        
                        return (
                          <select
                            value={currentTaxRate}
                            onChange={(e) => {
                              if (isNoVatInvoice) {
                                // Для фактур без VAT всегда ZW
                                updateItem(index, 'taxRate', TaxRate.ZW);
                              } else {
                                updateItem(index, 'taxRate', e.target.value as TaxRate);
                              }
                            }}
                            disabled={isReadOnly || isNoVatInvoice}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              isReadOnly || isNoVatInvoice ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          >
                            <option value={TaxRate.VAT_23}>{TaxRateLabels[TaxRate.VAT_23]}</option>
                            <option value={TaxRate.VAT_8}>{TaxRateLabels[TaxRate.VAT_8]}</option>
                            <option value={TaxRate.VAT_5}>{TaxRateLabels[TaxRate.VAT_5]}</option>
                            <option value={TaxRate.VAT_0}>{TaxRateLabels[TaxRate.VAT_0]}</option>
                            <option value={TaxRate.NP}>{TaxRateLabels[TaxRate.NP]}</option>
                            <option value={TaxRate.NPUE}>{TaxRateLabels[TaxRate.NPUE]}</option>
                            <option value={TaxRate.NA}>{TaxRateLabels[TaxRate.NA]}</option>
                            <option value={TaxRate.ZW}>{TaxRateLabels[TaxRate.ZW]}</option>
                          </select>
                        );
                      })()}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('invoices.unitOfMeasure')}
                      </label>
                      <select
                        value={item.unitOfMeasure}
                        onChange={(e) => updateItem(index, 'unitOfMeasure', e.target.value as UnitOfMeasure)}
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value={UnitOfMeasure.PIECE}>{t('invoices.unitOfMeasures.PIECE')}</option>
                        <option value={UnitOfMeasure.HOUR}>{t('invoices.unitOfMeasures.HOUR')}</option>
                        <option value={UnitOfMeasure.DAY}>{t('invoices.unitOfMeasures.DAY')}</option>
                        <option value={UnitOfMeasure.WEEK}>{t('invoices.unitOfMeasures.WEEK')}</option>
                        <option value={UnitOfMeasure.MONTH}>{t('invoices.unitOfMeasures.MONTH')}</option>
                        <option value={UnitOfMeasure.KG}>{t('invoices.unitOfMeasures.KG')}</option>
                        <option value={UnitOfMeasure.TON}>{t('invoices.unitOfMeasures.TON')}</option>
                        <option value={UnitOfMeasure.METER}>{t('invoices.unitOfMeasures.METER')}</option>
                        <option value={UnitOfMeasure.SQUARE_METER}>{t('invoices.unitOfMeasures.SQUARE_METER')}</option>
                        <option value={UnitOfMeasure.CUBIC_METER}>{t('invoices.unitOfMeasures.CUBIC_METER')}</option>
                        <option value={UnitOfMeasure.LITER}>{t('invoices.unitOfMeasures.LITER')}</option>
                        <option value={UnitOfMeasure.SET}>{t('invoices.unitOfMeasures.SET')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <div className="text-sm text-gray-600">
                      {t('invoices.total')}: {(item.quantity * item.unitPrice).toFixed(2)}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={isReadOnly}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('invoices.pdfLanguage')} *
              </label>
              <select
                value={formData.pdfLanguage}
                onChange={(e) => setFormData({ ...formData, pdfLanguage: e.target.value as Language })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {PDF_LANGUAGE_OPTIONS.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.nativeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('invoices.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              disabled={isReadOnly}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('invoices.internalComment')}
            </label>
            <textarea
              value={formData.internalComment}
              onChange={(e) => setFormData({ ...formData, internalComment: e.target.value })}
              rows={3}
              placeholder={t('invoices.internalCommentPlaceholder')}
              disabled={isReadOnly}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('invoices.internalCommentHint')}
            </p>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleDelete}
              disabled={isLoading || !canDeleteInvoice}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !canDeleteInvoice
                  ? (invoice.wfirmaId
                      ? (t('invoices.deleteInvoiceOnlyNotSentToWfirma') ||
                        'Нельзя удалить фактуру, которая уже была отправлена в wFirma.')
                      : (t('invoices.deleteInvoiceOnlySample') ||
                        'Удаление разрешено только для фактур со статусом Черновик.'))
                  : undefined
              }
            >
              {isLoading ? t('common.deleting') : t('common.delete')}
            </button>
            
            <div className="space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || isReadOnly}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
